import React, { useMemo } from 'react';
import { ChecklistItem, EquipmentAnomalySummaryItem, FVIEquipmentItem, FVIPDOChecklistItem, NewAnomalyItem, PDFImageProps } from '../../../../utils/models';
import { Reorder } from 'framer-motion';

import { getAnomalyStartNumber, getShowLastLineOption, matchPhrases, getIsSameOrAfter } from '../../../../utils/utilities';
import { MAX_INSPECTED_ITEMS_PER_REPORT, NEW_UTM_KICKOFF_DATE } from '../../../../utils/constants';
import { EQUIPMENT_ANOMALY, EQUIPMENT_CHECKLIST } from '../createCustomFVIEquipmentReport';

import Conditional from '../../../common/conditional';
import CustomNewItem from '../../custom-general/custom-new-item';
import EquipmentItemForm from '../../../forms/custom-fvi/equipmentItemForm';
import ExistingEquipmentItem from '../existing-equipment-item';
import ItemCount from '../../../blocks/item-count';
import ReorderItem from '../../../common/reorderItem';
import WarningNotice from '../warning-notice';
import Zippy from '../../../themed/zippy';

import useAnomalyRecords from '../../../../hooks/useAnomalyRecords';
import useIncompleteInspectedSections from '../../../../hooks/useIncompleteInspectedSections';
import useInspectedItemIDUpdate from '../../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../../hooks/useProjectDetails';

import toastService from '../../../../services/toastService';

interface Props {
    equipmentItems: FVIEquipmentItem[];
    onChangeEquipmentItems: (items: FVIEquipmentItem[]) => void;
}

const EquipmentItems: React.FC<Props> = ({ equipmentItems, onChangeEquipmentItems }) => {
	const { projectDetails } = useProjectDetails();
    const { incompleteSections, isLastInspectedItemComplete } = useIncompleteInspectedSections(equipmentItems)
    
    const handleChangeAdditionalComment = (equipmentNumber: string, comment: string) => {
        const newEquipmentItems = equipmentItems.map((item) => item.equipmentNumber === equipmentNumber ? ({
            id: item.id,
            anomalySummary: item.anomalySummary,
            equipmentNumber: item.equipmentNumber,
            equipmentDescription: item.equipmentDescription,
            pIDNumber: item.pIDNumber,
            pIDImages: item.pIDImages,
            additionalComment: comment,
            hasPDO: item.hasPDO!,
            pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
            checklistItems: item.checklistItems || EQUIPMENT_CHECKLIST,
            anomalyItems: item.anomalyItems,
            hasAnomalies: item.hasAnomalies,
            inspectionType: item.inspectionType,
            hasPID: item.hasPID,
            inspectionDescription: item.inspectionDescription,
            generalPhotos: item.generalPhotos,
            generalDrawing: item.generalDrawing,
        }) : item);

        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleChangePIDImage = (equipmentNumber: string, pIDData: Pick<FVIEquipmentItem, 'pIDImages' | 'pIDNumber'>) => {
        const newEquipmentItems = equipmentItems.map((item) =>
            item.equipmentNumber === equipmentNumber
                ? {
                        id: item.id,
                        equipmentNumber: item.equipmentNumber,
                        equipmentDescription: item.equipmentDescription,
                        anomalyItems: item.anomalyItems,
                        hasAnomalies: item.hasAnomalies,
                        hasPID: item.hasPID,
                        additionalComment: item.additionalComment,
                        hasPDO: item.hasPDO!,
                        pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
                        checklistItems: item.checklistItems || EQUIPMENT_CHECKLIST,
                        inspectionType: item.inspectionType,
                        anomalySummary: item.anomalySummary,
                        inspectionDescription: item.inspectionDescription,
                        generalPhotos: item.generalPhotos,
                        generalDrawing: item.generalDrawing,
                        pIDImages: pIDData.pIDImages,
                        pIDNumber: pIDData.pIDNumber,
                    }
                : item
        );

        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleChangePDOChecklist = (equipmentNumber: string, items: FVIPDOChecklistItem[]) => {
        const newEquipmentItems = equipmentItems.map((item) => item.equipmentNumber === equipmentNumber ? { ...item, pdoChecklist: items } : item);
        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleAddEquipmentItem = (data: Partial<FVIEquipmentItem>) => {
        const matchFound = equipmentItems.find(({ equipmentNumber }) => matchPhrases(equipmentNumber, data.equipmentNumber));
        if (matchFound) return toastService.error(`${data.equipmentNumber} has already been added!`);

        const newEquipmentItem: FVIEquipmentItem = {
            id: equipmentItems.length + 1,
            equipmentNumber: data.equipmentNumber!,
            hasPDO: data.hasPDO!,
            pdoChecklist: PDO_CHECKLIST,
            equipmentDescription: data.equipmentDescription,
            anomalyItems: [],
            pIDImages: [],
            checklistItems: EQUIPMENT_CHECKLIST,
            pIDNumber: '',
            additionalComment: '',
            inspectionType: data.inspectionType!,
            anomalySummary: EQUIPMENT_ANOMALY as any,
            hasPID: data.hasPID!,
            hasAnomalies: data.hasAnomalies!,
            inspectionDescription: data.inspectionDescription!,
            generalPhotos: [],
            generalDrawing: [],
        };

        const newEquipmentItems: FVIEquipmentItem[] = [...equipmentItems, newEquipmentItem];
        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleEditEquipmentItem = (initialItem: FVIEquipmentItem, updatedEquipmentItem: Partial<FVIEquipmentItem>) => {
        const matchFound = equipmentItems.filter(({ equipmentNumber }) => equipmentNumber !== initialItem.equipmentNumber)
        .find(({ equipmentNumber }) => matchPhrases(equipmentNumber, updatedEquipmentItem.equipmentNumber));

        if (matchFound) {
            return toastService.error(`${updatedEquipmentItem.equipmentNumber} has already been added!`);
        }

        const newEquipmentItems = equipmentItems.map((equipmentItem) => equipmentItem.equipmentNumber === initialItem.equipmentNumber ? ({
            ...equipmentItem,
            pdoChecklist: equipmentItem.pdoChecklist || PDO_CHECKLIST,
            checklistItems: equipmentItem.checklistItems || EQUIPMENT_CHECKLIST,
            equipmentNumber: updatedEquipmentItem.equipmentNumber!,
            equipmentDescription: updatedEquipmentItem.equipmentDescription,
            hasPID: updatedEquipmentItem.hasPID!,
            hasPDO: updatedEquipmentItem.hasPDO!,
            hasAnomalies: updatedEquipmentItem.hasAnomalies!,
            inspectionType: updatedEquipmentItem.inspectionType!,
            inspectionDescription: updatedEquipmentItem.inspectionDescription!,
        }) : equipmentItem)

        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleDeleteEquipmentItem = (equipmentNumber: string): void => {
        const newEquipmentItems = equipmentItems.filter((item) => item.equipmentNumber !== equipmentNumber);
        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleSaveAnomalySummary = (equipmentNumber: string, anomalySummary: EquipmentAnomalySummaryItem) => {
        const newEquipmentItems = equipmentItems.map((item) =>
            item.equipmentNumber === equipmentNumber
                ? {
                        id: item.id,
                        anomalySummary,
                        equipmentNumber: item.equipmentNumber,
                        equipmentDescription: item.equipmentDescription,
                        pIDNumber: item.pIDNumber,
                        pIDImages: item.pIDImages,
                        pdoChecklist: item.pdoChecklist,
                        hasPDO: item.hasPDO,
                        additionalComment: item.additionalComment,
                        checklistItems: item.checklistItems || EQUIPMENT_CHECKLIST,
                        anomalyItems: item.anomalyItems,
                        hasAnomalies: item.hasAnomalies,
                        inspectionType: item.inspectionType,
                        hasPID: item.hasPID,
                        inspectionDescription: item.inspectionDescription,
                        generalPhotos: item.generalPhotos,
                        generalDrawing: item.generalDrawing,
                    }
                : item
        );

        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleSetEquipmentAnomalyItems = (equipmentNumber: string, items: NewAnomalyItem[]) => {
        const newEquipmentItems = equipmentItems.map((item) =>
            item.equipmentNumber === equipmentNumber
                ? {
                        id: item.id,
                        pIDImages: item.pIDImages,
                        pIDNumber: item.pIDNumber,
                        hasPID: item.hasPID,
                        equipmentNumber: item.equipmentNumber,
                        equipmentDescription: item.equipmentDescription,
                        anomalyItems: items,
                        pdoChecklist: item.pdoChecklist,
                        hasPDO: item.hasPDO,
                        additionalComment: item.additionalComment,
                        checklistItems: item.checklistItems || EQUIPMENT_CHECKLIST,
                        inspectionType: item.inspectionType,
                        anomalySummary: item.anomalySummary,
                        inspectionDescription: item.inspectionDescription,
                        hasAnomalies: item.hasAnomalies,
                        generalPhotos: item.generalPhotos,
                        generalDrawing: item.generalDrawing,
                    }
                : item
        );

        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleSetChecklistItems = (equipmentNumber: string, items: ChecklistItem[]) => {
        const newEquipmentItems = equipmentItems.map((item) =>
            item.equipmentNumber === equipmentNumber
                    ? {
                        id: item.id,
                        anomalySummary: item.anomalySummary,
                        equipmentNumber: item.equipmentNumber,
                        equipmentDescription: item.equipmentDescription,
                        hasPID: item.hasPID,
                        pIDNumber: item.pIDNumber,
                        pIDImages: item.pIDImages,
                        pdoChecklist: item.pdoChecklist,
                        hasPDO: item.hasPDO,
                        additionalComment: item.additionalComment,
                        inspectionType: item.inspectionType,
                        anomalyItems: item.anomalyItems,
                        checklistItems: items,
                        hasAnomalies: item.hasAnomalies,
                        inspectionDescription: item.inspectionDescription,
                        generalPhotos: item.generalPhotos,
                        generalDrawing: item.generalDrawing,
                    }
                : item
        );

        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleSetEquipmentGeneralPhotos = (equipmentNumber: string, images: PDFImageProps[]) => {
        const newEquipmentItems = equipmentItems.map((item) =>
            item.equipmentNumber === equipmentNumber
                ? {
                        id: item.id,
                        pIDNumber: item.pIDNumber,
                        pIDImages: item.pIDImages,
                        hasPID: item.hasPID,
                        checklistItems: item.checklistItems || EQUIPMENT_CHECKLIST,
                        inspectionType: item.inspectionType,
                        equipmentNumber: item.equipmentNumber,
                        equipmentDescription: item.equipmentDescription,
                        additionalComment: item.additionalComment,
                        pdoChecklist: item.pdoChecklist,
                        hasPDO: item.hasPDO,
                        anomalyItems: item.anomalyItems,
                        anomalySummary: item.anomalySummary,
                        inspectionDescription: item.inspectionDescription,
                        hasAnomalies: item.hasAnomalies,
                        generalPhotos: images,
                        generalDrawing: item.generalDrawing,
                    }
                : item
        );

        onChangeEquipmentItems(newEquipmentItems);
    };

    const handleSetEquipmentGeneralDrawing = (equipmentNumber: string, images: PDFImageProps[]) => {
        const newEquipmentItems = equipmentItems.map((item) =>
            item.equipmentNumber === equipmentNumber
                ? {
                        id: item.id,
                        pIDNumber: item.pIDNumber,
                        pIDImages: item.pIDImages,
                        hasPID: item.hasPID,
                        checklistItems: item.checklistItems || EQUIPMENT_CHECKLIST,
                        inspectionType: item.inspectionType,
                        equipmentNumber: item.equipmentNumber,
                        equipmentDescription: item.equipmentDescription,
                        additionalComment: item.additionalComment,
                        pdoChecklist: item.pdoChecklist,
                        hasPDO: item.hasPDO,
                        anomalyItems: item.anomalyItems,
                        anomalySummary: item.anomalySummary,
                        inspectionDescription: item.inspectionDescription,
                        hasAnomalies: item.hasAnomalies,
                        generalPhotos: item.generalPhotos,
                        generalDrawing: images,
                    }
                : item
        );

        onChangeEquipmentItems(newEquipmentItems);
    };

    const isNewFormat = useMemo(() => {
        if (!projectDetails?.createdAt) return false;

        return getIsSameOrAfter(projectDetails.createdAt, NEW_UTM_KICKOFF_DATE)
    }, [projectDetails?.createdAt]);

    useInspectedItemIDUpdate(equipmentItems.map((item) => item.equipmentNumber));
    useAnomalyRecords(equipmentItems.flatMap((equipment) => equipment.anomalyItems));

    return ( 
        <Zippy headerTitle="Inspected Items" isValid={equipmentItems.length > 0}>
            <ItemCount count={equipmentItems.length} label='Inspected Equipment Items' />

            <Conditional isVisible={isLastInspectedItemComplete && equipmentItems.length <= MAX_INSPECTED_ITEMS_PER_REPORT}>
                <CustomNewItem label='Add New Equipment Item'>
                    <EquipmentItemForm onAdd={handleAddEquipmentItem} showLastLineOption={getShowLastLineOption(equipmentItems, equipmentItems.length - 1)} />
                </CustomNewItem>
            </Conditional>

            <Conditional isVisible={!isLastInspectedItemComplete}>
                <WarningNotice 
                    title='Equipment Information Incomplete' 
                    body={`Please complete sections ${incompleteSections.join(', ')}`} 
                />
            </Conditional>

            <Reorder.Group className='list-unstyled' values={equipmentItems} onReorder={onChangeEquipmentItems}>
                {equipmentItems.map((item, index) => (
                    <ReorderItem key={item.equipmentNumber} value={item}>
                        <ExistingEquipmentItem
                            isNewFormat={isNewFormat}
                            key={item.equipmentNumber}
                            showLastLineOption={getShowLastLineOption(equipmentItems, index)}
                            nextAnomalyID={getAnomalyStartNumber(equipmentItems)}
                            equipmentItem={item}
                            onChangePDOChecklist={handleChangePDOChecklist}
                            onChangePID={handleChangePIDImage}
                            onChangeAdditionalComment={handleChangeAdditionalComment}
                            onDeleteEquipmentItem={handleDeleteEquipmentItem}
                            onEditEquipmentItem={handleEditEquipmentItem}
                            onChangeChecklistItems={handleSetChecklistItems}
                            onChangeEquipmentAnomaly={handleSetEquipmentAnomalyItems}
                            onChangeGeneralPhotos={handleSetEquipmentGeneralPhotos}
                            onChangeGeneralDrawing={handleSetEquipmentGeneralDrawing}
                            onSaveAnomalySummary={handleSaveAnomalySummary}
                        />
                    </ReorderItem>
                ))}
            </Reorder.Group>
        </Zippy>
     );
};

export const PDO_CHECKLIST: FVIPDOChecklistItem[] = [
    {
        id: '1',
        label: 'Are there any pre-existing dropped object hazards in the work area, such as loose items or debris?',
        value: '',
        category: 'general',
    },
    {
        id: '2',
        label: 'Are all tools properly fastened and adequately secured?',
        value: '',
        category: 'general',
    },
    {
        id: '3',
        label: 'Are equipment, objects, and tools properly secured or tied down to prevent impact or displacement due to windy or inclement weather?',
        value: '',
        category: 'general',
    },
    {
        id: '4',
        label: 'Are there any visible signs of floor gaps, damaged grating, mesh, or handrails?',
        value: '',
        category: 'general',
    },
    {
        id: '5',
        label: 'Are tools and tackles adequately secured while working at height?',
        value: '',
        category: 'general',
    },
    {
        id: '6',
        label: 'Are there any moving machinery, cranes, or vehicles in the travel path that could cause collisions or load shifts?',
        value: '',
        category: 'general',
    },
    {
        id: '7',
        label: 'Is there any poor housekeeping after completion of work?',
        value: '',
        category: 'general',
    },
    {
        id: '8',
        label: 'Are vessel supports in good condition (clamps, cleats, and foundation bolts)?',
        value: '',
        category: 'components',
    },
    {
        id: '9',
        label: 'Are the connections to level gauges, instrumentation, pressure relief valves, and other related equipment intact?',
        value: '',
        category: 'components',
    },
    {
        id: '10',
        label: 'Are there any signs of corrosion on supporting structures or piping connected to ancillary equipment?',
        value: '',
        category: 'components',
    },
    {
        id: '11',
        label: 'For insulated vessels, check the condition of cleats and structural components protruding outside the insulation. Check for any damaged insulation cladding sheets.',
        value: '',
        category: 'components',
    },
    {
        id: '12',
        label: 'Is the integrity of the skirt, saddles, etc., intact?',
        value: '',
        category: 'components',
    },
    {
        id: '13',
        label: 'For heavily vibrating services or structures, are the bolts properly tightened?',
        value: '',
        category: 'components',
    },
    {
        id: '14',
        label: 'Is the equipment nameplate intact?',
        value: '',
        category: 'components',
    },
];

export default EquipmentItems;